import React, { useEffect, useMemo, useRef } from 'react';
import { CalendarEventResponse } from '@troia-v3/types';
import { assignEventColumns } from '../utils/eventLayout';
import { eventOccursOnDate } from '../utils/eventDate';
import { EventHoverCard } from './EventHoverCard';
import { Icon } from '@/shared/icons';

interface DayViewProps {
  currentDate: Date;
  events: CalendarEventResponse[];
  onEventClick: (event: CalendarEventResponse) => void;
  onSlotClick: (date: Date, hour: number, minute: number) => void;
}

const MIN_EVENT_CARD_HEIGHT = 30;
const DEFAULT_PPM = 1;
const TOTAL_MINUTES = 24 * 60;
const SLOTS_COUNT = 48;
const SLOT_MINUTES = 30;

interface DayLayout {
  slotHeights: number[];
  eventPositions: Map<string, { top: number; height: number }>;
  minuteToPixel: (minute: number) => number;
}

function calculateDayLayout(dayEvents: CalendarEventResponse[]): DayLayout {
  // Step 1: Build pixels-per-minute array
  const ppm = new Float64Array(TOTAL_MINUTES);
  ppm.fill(DEFAULT_PPM);

  for (const event of dayEvents) {
    const start = new Date(event.startTime);
    const end = new Date(event.endTime);
    const startMin = start.getHours() * 60 + start.getMinutes();
    let endMin = end.getHours() * 60 + end.getMinutes();
    if (endMin === startMin) endMin = Math.min(startMin + 30, TOTAL_MINUTES);
    else if (endMin < startMin) endMin = TOTAL_MINUTES;
    const duration = endMin - startMin;

    if (duration > 0 && duration < MIN_EVENT_CARD_HEIGHT) {
      const neededPPM = MIN_EVENT_CARD_HEIGHT / duration;
      for (let m = startMin; m < endMin; m++) {
        if (neededPPM > ppm[m]) {
          ppm[m] = neededPPM;
        }
      }
    }
  }

  // Step 2: Build cumulative pixel array
  const cumulativePixels = new Float64Array(TOTAL_MINUTES + 1);
  cumulativePixels[0] = 0;
  for (let m = 0; m < TOTAL_MINUTES; m++) {
    cumulativePixels[m + 1] = cumulativePixels[m] + ppm[m];
  }

  const minuteToPixel = (minute: number): number => {
    const clamped = Math.max(0, Math.min(minute, TOTAL_MINUTES));
    return cumulativePixels[clamped];
  };

  // Step 3: Derive slot heights
  const slotHeights: number[] = [];
  for (let i = 0; i < SLOTS_COUNT; i++) {
    const slotStart = i * SLOT_MINUTES;
    const slotEnd = (i + 1) * SLOT_MINUTES;
    slotHeights.push(cumulativePixels[slotEnd] - cumulativePixels[slotStart]);
  }

  // Step 4: Position events
  const eventPositions = new Map<string, { top: number; height: number }>();
  for (const event of dayEvents) {
    const start = new Date(event.startTime);
    const end = new Date(event.endTime);
    const startMin = start.getHours() * 60 + start.getMinutes();
    let endMin = end.getHours() * 60 + end.getMinutes();
    if (endMin === startMin) endMin = Math.min(startMin + 30, TOTAL_MINUTES);
    else if (endMin < startMin) endMin = TOTAL_MINUTES;

    const top = cumulativePixels[startMin];
    const rawHeight = cumulativePixels[endMin] - top;
    const height = Math.max(rawHeight, MIN_EVENT_CARD_HEIGHT);

    eventPositions.set(event.id, { top, height });
  }

  return { slotHeights, eventPositions, minuteToPixel };
}

export const DayView: React.FC<DayViewProps> = ({
  currentDate,
  events,
  onEventClick,
  onSlotClick,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Generate hours with 30-minute slots
  const timeSlots: { hour: number; minute: number }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    timeSlots.push({ hour, minute: 0 });
    timeSlots.push({ hour, minute: 30 });
  }

  const dayEvents = useMemo(() => {
    return events.filter(event => eventOccursOnDate(event, currentDate));
  }, [events, currentDate]);

  const isToday = (): boolean => {
    const today = new Date();
    return (
      currentDate.getDate() === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const getCurrentTimePosition = (): number | null => {
    if (!isToday()) return null;

    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    return hours * 60 + minutes;
  };

  const formatTime = (hour: number, minute: number): string => {
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  const formatDateHeader = (): string => {
    const weekDays = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

    return `${weekDays[currentDate.getDay()]}, ${currentDate.getDate()} de ${months[currentDate.getMonth()]} de ${currentDate.getFullYear()}`;
  };

  const layout = useMemo(() => calculateDayLayout(dayEvents), [dayEvents]);
  const columnLayout = useMemo(() => assignEventColumns(dayEvents), [dayEvents]);

  // Map Google Calendar Color IDs (1-11) to hex colors
  const getEventColor = (colorId?: string, eventType?: string): string => {
    if (eventType === 'birthday') return '#10b981';
    if (eventType === 'activity') return '#f97316';

    const colors: Record<string, string> = {
      '1': '#a4bdfc',
      '2': '#7ae7bf',
      '3': '#dbadff',
      '4': '#ff887c',
      '5': '#fbd75b',
      '6': '#ffb878',
      '7': '#46d6db',
      '8': '#e1e1e1',
      '9': '#5484ed',
      '10': '#51b749',
      '11': '#dc2127',
    };
    return colors[colorId || '9'] || colors['9'];
  };

  const formatEventTime = (startTime: string, endTime: string): string => {
    const start = new Date(startTime);
    const end = new Date(endTime);

    const formatHour = (date: Date) => {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    return `${formatHour(start)} - ${formatHour(end)}`;
  };

  // Auto-scroll to current time on mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      const currentMinutes = getCurrentTimePosition();
      if (currentMinutes !== null) {
        const scrollPosition = layout.minuteToPixel(currentMinutes) - 100;
        scrollContainerRef.current.scrollTop = Math.max(0, scrollPosition);
      } else {
        scrollContainerRef.current.scrollTop = layout.minuteToPixel(8 * 60);
      }
    }
  }, [currentDate, layout]);

  const currentTimePosition = getCurrentTimePosition();

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--theme-card-background)' }}>
      {/* Header with date */}
      <div
        className="py-3 px-4 border-b sticky top-0 z-10"
        style={{
          borderColor: 'var(--theme-border)',
          backgroundColor: 'var(--theme-card-background)',
        }}
      >
        <div
          className="text-base font-bold capitalize"
          style={{ color: isToday() ? 'var(--theme-primary)' : 'var(--theme-card-text)' }}
        >
          {formatDateHeader()}
        </div>
      </div>

      {/* Scrollable timeline */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto relative">
        <div className="flex">
          {/* Time labels column */}
          <div className="w-20 flex-shrink-0 border-r relative" style={{ borderColor: 'var(--theme-border)' }}>
            {timeSlots.map((slot, index) => (
              <div
                key={index}
                className="border-b text-xs text-right pr-2"
                style={{
                  height: `${layout.slotHeights[index]}px`,
                  borderColor: 'var(--theme-border)',
                  color: 'var(--theme-muted-text)',
                  paddingTop: slot.minute === 0 ? '4px' : '0',
                  opacity: slot.minute === 0 ? 1 : 0.5,
                }}
              >
                {slot.minute === 0 ? formatTime(slot.hour, slot.minute) : ''}
              </div>
            ))}
          </div>

          {/* Day column with time slots */}
          <div className="flex-1 relative">
            {/* Time slots */}
            {timeSlots.map((slot, index) => (
              <div
                key={index}
                className="border-b cursor-pointer hover:bg-opacity-50 transition-colors"
                style={{
                  height: `${layout.slotHeights[index]}px`,
                  borderColor: 'var(--theme-border)',
                  borderStyle: slot.minute === 0 ? 'solid' : 'dashed',
                  backgroundColor: 'transparent',
                }}
                onClick={() => onSlotClick(currentDate, slot.hour, slot.minute)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--theme-input-background)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              />
            ))}

            {/* Events overlay */}
            <div className="absolute inset-0 pointer-events-none">
              {dayEvents.map((event) => {
                const position = layout.eventPositions.get(event.id);
                const colInfo = columnLayout.get(event.id);
                if (!position) return null;
                const { top, height } = position;
                const col = colInfo?.column ?? 0;
                const totalCols = colInfo?.totalColumns ?? 1;
                const leftPercent = (col / totalCols) * 100;
                const widthPercent = (1 / totalCols) * 100;
                const eventColor = getEventColor(event.colorId, event.eventType);
                const cancelled = event.status === 'cancelled';

                return (
                  <EventHoverCard key={event.id} event={event}>
                  <div
                    className="absolute rounded-lg px-2 py-1 overflow-hidden cursor-pointer pointer-events-auto hover:shadow-lg transition-shadow"
                    style={{
                      top: `${top}px`,
                      height: `${height}px`,
                      left: `calc(${leftPercent}% + 2px)`,
                      width: `calc(${widthPercent}% - 4px)`,
                      backgroundColor: `${eventColor}29`,
                      color: eventColor,
                      borderLeft: `3px solid ${eventColor}`,
                      zIndex: 5,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                  >
                    <div
                      className="text-sm font-semibold truncate"
                      style={cancelled ? { textDecoration: 'line-through' } : undefined}
                    >
                      {event.summary}
                    </div>
                    {height >= 40 && (
                      <div className="text-xs opacity-90 truncate">
                        {formatEventTime(event.startTime, event.endTime)}
                      </div>
                    )}
                    {event.location && height >= 60 && (
                      <div className="text-xs opacity-80 truncate flex items-center gap-1">
                        <Icon name="map-pin" className="w-3 h-3 flex-shrink-0" />
                        {event.location}
                      </div>
                    )}
                    {event.description && height > 80 && (
                      <div className="text-xs opacity-70 line-clamp-2 mt-1">
                        {event.description}
                      </div>
                    )}
                  </div>
                  </EventHoverCard>
                );
              })}
            </div>

            {/* Current time indicator (red line) */}
            {isToday() && currentTimePosition !== null && (
              <div
                className="absolute left-0 right-0 pointer-events-none"
                style={{
                  top: `${layout.minuteToPixel(currentTimePosition)}px`,
                  zIndex: 10,
                }}
              >
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: '#EF4444' }}
                  />
                  <div
                    className="flex-1 h-0.5"
                    style={{ backgroundColor: '#EF4444' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
